import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, TextField, InputAdornment, Chip, CircularProgress, Alert,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, Tooltip, Avatar,
  List, ListItemButton, ListItemAvatar, ListItemText, Divider, Badge, Tabs, Tab, useMediaQuery,
  useTheme, Snackbar, MenuItem, Select, FormControl, InputLabel, Menu, Switch,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Search as SearchIcon, Refresh as RefreshIcon, Send as SendIcon,
  Pause as PauseIcon, PlayArrow as ResumeIcon, Phone as PhoneIcon,
  Close as CloseIcon, Circle as CircleIcon, SmartToy as BotIcon,
  ArrowBack as BackIcon, NotificationsActive as NotifIcon,
  Person as PersonIcon, SupportAgent as AgentIcon,
  DirectionsCar as CarIcon, AttachMoney as MoneyIcon,
  LocationOn as LocationIcon, AccessTime as FollowUpIcon,
  Label as LabelIcon, Note as NoteIcon, EventNote as EventIcon,
  Info as InfoIcon, Add as AddIcon, Delete as DeleteIcon,
  Schedule as ScheduleIcon, CheckCircle as CheckIcon,
  Cancel as CancelIcon, ContentCopy as CopyIcon,
  Route as RouteIcon, Groups as GroupsIcon,
  Undo as UndoIcon, ClearAll as ClearAllIcon,
  AddCircleOutline as AddToQueueIcon,
} from '@mui/icons-material';
import { ViewKanban as KanbanIcon, ViewList as ListIcon, Archive as ArchiveIcon } from '@mui/icons-material';
import { apiService } from '../../services/api';
import LeadsKanbanBoard from './LeadsKanbanBoard';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function timeAgo(date: string | Date): string {
  const now = new Date().getTime();
  const then = new Date(date).getTime();
  const diff = Math.floor((now - then) / 1000);
  if (diff < 60) return `hace ${diff}s`;
  if (diff < 3600) return `hace ${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)}h`;
  return `hace ${Math.floor(diff / 86400)}d`;
}

function formatTimestamp(ts: string | number | Date): string {
  const d = typeof ts === 'number' ? new Date(ts * 1000) : new Date(ts);
  return d.toLocaleString('es-DO', { day: '2-digit', month: '2-digit', hour: 'numeric', minute: '2-digit', hour12: true });
}

function getInitials(name: string): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
}

// Desde 2026 hay clientes que escriben sin compartir su número: Meta los identifica
// con un BSUID (`DO.1043717294941446`). Mostrarlo crudo no le dice nada a nadie.
const esBsuid = (id?: string) => !!id && /^[A-Za-z]{2}\.[A-Za-z0-9_-]+$/.test(id);

function formatPhone(phone: string): string {
  if (!phone) return '';
  if (esBsuid(phone)) return 'Sin número (usuario de WhatsApp)';
  const clean = phone.replace(/\D/g, '');
  if (clean.length === 11 && clean.startsWith('1')) {
    return `(${clean.slice(1, 4)}) ${clean.slice(4, 7)}-${clean.slice(7)}`;
  }
  return phone;
}

function followUpLabel(dateStr?: string): string {
  if (!dateStr) return 'Seguimiento';
  const target = new Date(dateStr);
  if (!Number.isFinite(target.getTime()) || target.getTime() <= 0) return 'Seguimiento';
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrowStart = new Date(todayStart.getTime() + 86400000);
  if (target.getTime() < todayStart.getTime()) return 'Vencido';
  if (target.getTime() < tomorrowStart.getTime()) return 'Hoy';
  return target.toLocaleDateString('es-DO', { day: '2-digit', month: '2-digit' });
}

/**
 * Horas desde el último mensaje **del cliente**, que es lo que abre la ventana de servicio
 * de Meta.
 *
 * Antes se medía con `lastMessageAt`, que es el último mensaje de cualquiera: bastaba que el
 * operador o el bot respondiera para que la cuenta se reiniciara y el chat se viera abierto
 * con la ventana cerrada hacía rato. El resultado era escribir, darle a enviar y recibir el
 * rechazo de Meta — justo lo que el aviso quería evitar.
 */
function horasDesdeElCliente(convo?: { messages?: any[]; lastMessageAt?: string }): number | null {
  const msgs = convo?.messages || [];
  for (let i = msgs.length - 1; i >= 0; i--) {
    const m = msgs[i];
    if (m?.sender !== 'client') continue;
    const t = typeof m.timestamp === 'number'
      ? m.timestamp * (m.timestamp < 1e12 ? 1000 : 1)
      : new Date(m.timestamp).getTime();
    if (t && !isNaN(t)) return (Date.now() - t) / 3600000;
  }
  // Sin historial cargado: `lastMessageAt` es lo único que hay. Sobreestima la ventana
  // cuando el último mensaje fue nuestro, así que solo sirve de respaldo.
  if (convo?.lastMessageAt) {
    const t = new Date(convo.lastMessageAt).getTime();
    if (!isNaN(t)) return (Date.now() - t) / 3600000;
  }
  return null;
}

function isWindowExpired(convo?: { messages?: any[]; lastMessageAt?: string }): boolean {
  const h = horasDesdeElCliente(convo);
  return h === null || h > 24;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Conversation {
  phoneNumber: string;
  contactName: string;
  leadName: string;
  leadVehicleCount: number;
  flowStage: string;
  status: string;
  lastBody: string;
  lastSender: string;
  lastMessageAt: string;
  messagesCount: number;
  botPaused: boolean;
  clientZone: string;
  moneyMentioned: boolean;
  pendingFollowUp?: string;
  pendingFollowUpAt?: string;
  clientLocation?: string;
  adminNotes?: string;
}

interface Message {
  body: string;
  sender: 'client' | 'bot' | 'assistant' | 'operator';
  timestamp: string | number;
  messageId?: string;
}

interface ConversationDetail extends Conversation {
  messages: Message[];
  botPausedUntil?: string;
  botPausedReason?: string;
  leadBusinessType?: string;
  leadUrgency?: string;
  adSource?: string;
  adHeadline?: string;
  createdAt?: string;
  routeQueueStatus?: string;
  routeQueueZone?: string;
}

interface ScheduledAction {
  actionId: string;
  type: string;
  target: { phone: string; name: string };
  action: { type: string; message: string };
  schedule: { scheduledFor: string; status: string; createdAt: string };
  createdBy: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STAGE_LABELS: Record<string, { label: string; color: any }> = {
  new: { label: 'Nuevo', color: 'info' },
  greeting: { label: 'Saludo', color: 'default' },
  qualifying: { label: 'Calificando', color: 'warning' },
  quoting: { label: 'Cotizando', color: 'primary' },
  closing: { label: 'Cerrando', color: 'success' },
  closed: { label: 'Cerrado', color: 'success' },
  lost: { label: 'Perdido', color: 'error' },
};

const STATUS_OPTIONS = [
  { value: 'active', label: 'Activo', color: '#4caf50' },
  { value: 'interested', label: 'Interesado', color: '#2196f3' },
  { value: 'scheduled', label: 'Agendado', color: '#9c27b0' },
  { value: 'escalated', label: 'Escalado', color: '#f44336' },
  { value: 'closed', label: 'Cerrado', color: '#757575' },
];

const LABEL_OPTIONS = [
  'Lead Nuevo', 'En Seguimiento', 'Cita Agendada', 'Cliente', 'Perdido',
  'Caliente', 'Tibio', 'No Interesado',
  'Ruta Cibao', 'Ruta Sur', 'Ruta Este', 'Santo Domingo',
  'VIP', 'Escalado', 'No Molestar', 'Soporte',
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const LeadsCRMPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams, setSearchParams] = useSearchParams();

  // Data
  const [viewMode, setViewMode] = useState<'list' | 'board'>('list');
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConvo, setSelectedConvo] = useState<ConversationDetail | null>(null);
  const [botStatus, setBotStatus] = useState<any>(null);
  // Interruptor global del bot (solo apaga respuestas automáticas)
  const [globalPaused, setGlobalPaused] = useState(false);
  const [globalPauseBy, setGlobalPauseBy] = useState<string | null>(null);
  const [globalPauseBusy, setGlobalPauseBusy] = useState(false);
  const [confirmGlobalPause, setConfirmGlobalPause] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  // Filters
  const [search, setSearch] = useState('');
  const [filterTab, setFilterTab] = useState(0);
  const [filterStage, setFilterStage] = useState('');
  const [filterZone, setFilterZone] = useState('');
  const [searchTimeout, setSearchTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);

  // Send message
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);

  // Pause dialog
  const [pauseDialog, setPauseDialog] = useState(false);
  const [pauseMinutes, setPauseMinutes] = useState(30);

  // Mobile
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false);
  // En móvil la ficha del lead se despliega sobre el chat, no debajo
  const [mobileInfoOpen, setMobileInfoOpen] = useState(false);

  // Side panel
  const [sideTab, setSideTab] = useState(0); // 0=Info, 1=Seguimientos, 2=Notas
  const [labels, setLabels] = useState<string[]>([]);
  const [actions, setActions] = useState<ScheduledAction[]>([]);
  const [notes, setNotes] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesTimeout, setNotesTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [labelMenuAnchor, setLabelMenuAnchor] = useState<null | HTMLElement>(null);

  // Follow-up creation
  const [newActionDialog, setNewActionDialog] = useState(false);
  const [newActionMessage, setNewActionMessage] = useState('');
  const [newActionDate, setNewActionDate] = useState('');

  // Cita tentativa (al marcar el lead como "Agendado")
  const [tentativeOpen, setTentativeOpen] = useState(false);
  const [tentativeName, setTentativeName] = useState('');
  const [tentativeDate, setTentativeDate] = useState('');
  const [tentativeTime, setTentativeTime] = useState('');
  const [tentativeNote, setTentativeNote] = useState('');
  const [tentativeSaving, setTentativeSaving] = useState(false);

  // Agenda
  const [agendaData, setAgendaData] = useState<any>(null);

  // Route queue
  const [routeQueue, setRouteQueue] = useState<any[]>([]);
  const [routeQueueConfig, setRouteQueueConfig] = useState<Record<string, { tentativeDate?: string }>>({});
  const [confirmRouteDialog, setConfirmRouteDialog] = useState<{ open: boolean; zone: string }>({ open: false, zone: '' });
  const [confirmRouteDate, setConfirmRouteDate] = useState('');
  const [confirmRouteMessage, setConfirmRouteMessage] = useState('');

  // Chat scroll ref
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Client correlation
  const [linkedClient, setLinkedClient] = useState<any>(null);

  // -----------------------------------------------------------------------
  // Fetch conversations
  // -----------------------------------------------------------------------

  const fetchConversations = useCallback(async () => {
    try {
      if (filterTab === 1) {
        const result = await apiService.getBotFollowups();
        setAgendaData(result.data || { followUps: [], scheduledActions: [] });
        setConversations([]);
        setLoading(false);
        return;
      }
      if (filterTab === 2) {
        const [qRes, cRes] = await Promise.all([
          apiService.getRouteQueue(),
          apiService.getRouteQueueConfig(),
        ]);
        const queueData = qRes.data || qRes;
        setRouteQueue(queueData.items || queueData || []);
        const cfgData = cRes.data || cRes;
        setRouteQueueConfig(cfgData.config || {});
        setConversations([]);
        setLoading(false);
        return;
      }
      const params: Record<string, any> = { limit: 50 };
      if (search) params.search = search;
      if (filterTab === 3) params.status = 'active';
      if (filterTab === 4) params.botPaused = 'true';
      if (filterTab === 5) params.status = 'escalated';
      if (filterStage) params.flowStage = filterStage;
      if (filterZone) params.clientZone = filterZone;

      const result = await apiService.getBotConversations(params);
      let data = result.data || [];
      if (filterTab === 6) data = data.filter((c: Conversation) => !!c.pendingFollowUp);
      setConversations(data);
    } catch (e: any) {
      console.error('Fetch error:', e.message);
    } finally {
      setLoading(false);
    }
  }, [search, filterTab, filterStage, filterZone]);

  const fetchBotStatus = useCallback(async () => {
    try {
      const result = await apiService.getBotStatus();
      setBotStatus(result?.data?.status || result?.data || result);
    } catch {} // eslint-disable-line
  }, []);

  const fetchGlobalPause = useCallback(async () => {
    try {
      const r = await apiService.getGlobalPause();
      const d = r?.data || r;
      setGlobalPaused(!!d?.paused);
      setGlobalPauseBy(d?.pausedBy || null);
    } catch {} // eslint-disable-line
  }, []);

  const toggleGlobalPause = useCallback(async (apagar: boolean) => {
    setGlobalPauseBusy(true);
    try {
      await apiService.setGlobalPause(apagar);
      setGlobalPaused(apagar);
      setSnackbar({
        open: true,
        message: apagar
          ? 'Bot apagado — no responderá mensajes. Los follow-ups siguen activos.'
          : 'Bot encendido — vuelve a responder normalmente.',
        severity: 'success',
      });
      fetchGlobalPause();
    } catch (e: any) {
      setSnackbar({ open: true, message: e.message || 'No se pudo cambiar el estado del bot', severity: 'error' });
    } finally {
      setGlobalPauseBusy(false);
      setConfirmGlobalPause(false);
    }
  }, [fetchGlobalPause]);

  useEffect(() => { fetchConversations(); fetchBotStatus(); fetchGlobalPause(); }, [fetchConversations, fetchBotStatus, fetchGlobalPause]);

  // Polling fallback (reduced to 2min since SSE is primary)
  useEffect(() => {
    const interval = setInterval(() => { fetchConversations(); fetchBotStatus(); fetchGlobalPause(); }, 120000);
    return () => clearInterval(interval);
  }, [fetchConversations, fetchBotStatus, fetchGlobalPause]);

  // SSE real-time connection
  const selectedPhoneRef = useRef<string | null>(null);
  useEffect(() => { selectedPhoneRef.current = selectedConvo?.phoneNumber || null; }, [selectedConvo]);

  // Los navegadores del teléfono ignoran (o niegan de una) el pedido de permiso
  // automático al cargar: solo lo conceden si sale de un toque del usuario. Por eso
  // aquí solo se LEE el estado; pedirlo es cosa del botón de la campana.
  const [permisoAviso, setPermisoAviso] = useState<string>(
    typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'
  );

  const pedirPermisoAviso = useCallback(async () => {
    if (typeof Notification === 'undefined') return;
    try {
      const r = await Notification.requestPermission();
      setPermisoAviso(r);
      setSnackbar({
        open: true,
        message: r === 'granted'
          ? 'Avisos activados. Te llegarán mientras tengas la página abierta.'
          : 'El navegador no dio permiso. Igual verás el contador en la pestaña y sentirás la vibración.',
        severity: r === 'granted' ? 'success' : 'error',
      });
    } catch {}
  }, []);

  // Contador de no leídos en el título de la pestaña. Es lo único que se ve
  // igual en todos los teléfonos, con o sin permiso de notificaciones.
  const [noLeidos, setNoLeidos] = useState(0);
  useEffect(() => {
    const base = 'CRM Chat — Driports';
    document.title = noLeidos > 0 ? `(${noLeidos}) ${base}` : base;
  }, [noLeidos]);

  useEffect(() => {
    const alVolver = () => { if (!document.hidden) setNoLeidos(0); };
    document.addEventListener('visibilitychange', alVolver);
    return () => document.removeEventListener('visibilitychange', alVolver);
  }, []);

  /** Aviso al llegar un mensaje del cliente: vibra y suena, sin depender de permisos. */
  const avisar = useCallback(() => {
    try { navigator.vibrate?.([120, 60, 120]); } catch {}
    try {
      // Un bip corto generado al vuelo; así no hay que servir ningún archivo de audio.
      const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const osc = ctx.createOscillator();
      const vol = ctx.createGain();
      osc.frequency.value = 880;
      vol.gain.setValueAtTime(0.0001, ctx.currentTime);
      vol.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.01);
      vol.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
      osc.connect(vol); vol.connect(ctx.destination);
      osc.start(); osc.stop(ctx.currentTime + 0.26);
      setTimeout(() => ctx.close().catch(() => {}), 400);
    } catch {}
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8090/api';
    let es: EventSource | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let closed = false;

    function connect() {
      if (closed) return;
      es = new EventSource(`${baseUrl}/bot/events?token=${token}`);

      es.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'new-message') {
            if (data.phone && data.phone === selectedPhoneRef.current) {
              setSelectedConvo(prev => {
                if (!prev) return prev;
                // Todo el sistema maneja segundos Unix; el respaldo también.
                const tsSeg = Number(data.timestamp) || Math.floor(Date.now() / 1000);
                const newMsg: Message = { body: data.body, sender: data.sender, timestamp: tsSeg };
                // 5s de tolerancia, en segundos (antes comparaba contra milisegundos y nunca casaba)
                const exists = prev.messages?.some(m => m.body === data.body && m.sender === data.sender && Math.abs(Number(m.timestamp) - tsSeg) < 5);
                if (exists) return prev;
                // Refrescar lastMessageAt es lo que reabre la ventana de 24h: sin esto,
                // el chat abierto seguía diciendo "expirada" aunque el cliente acabara
                // de escribir, y el campo de texto quedaba bloqueado hasta recargar.
                return {
                  ...prev,
                  messages: [...(prev.messages || []), newMsg],
                  lastMessageAt: new Date(tsSeg * 1000).toISOString(),
                };
              });
            }
            fetchConversations();

            if (data.sender === 'client') {
              const esElChatAbierto = data.phone && data.phone === selectedPhoneRef.current;
              // Solo cuenta y avisa si no lo estás viendo ya en pantalla.
              if (document.hidden || !esElChatAbierto) {
                setNoLeidos(n => n + 1);
                avisar();
              }
              if (typeof Notification !== 'undefined' && Notification.permission === 'granted' && document.hidden) {
                const phone = data.phone?.replace(/^1(\d{3})(\d{3})(\d{4})$/, '($1) $2-$3') || data.phone;
                const n = new Notification(`Nuevo mensaje - ${data.name || phone}`, {
                  body: (data.body || '').substring(0, 100),
                  icon: '/logo192.png',
                  tag: `msg-${data.phone}`,
                  // El teléfono debe vibrar aunque la notificación llegue agrupada
                  ...(({ vibrate: [120, 60, 120] }) as any),
                });
                n.onclick = () => { window.focus(); n.close(); };
              }
            }
          }
        } catch {}
      };

      es.onerror = () => {
        es?.close();
        retryTimer = setTimeout(connect, 5000);
      };
    }

    connect();
    return () => {
      closed = true;
      if (retryTimer) clearTimeout(retryTimer);
      es?.close();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // -----------------------------------------------------------------------
  // Select conversation + load side panel data
  // -----------------------------------------------------------------------

  const handleSelectConvo = useCallback(async (phone: string) => {
    setLoadingDetail(true);
    setLinkedClient(null);
    try {
      const result = await apiService.getBotConversation(phone);
      const detail = result.data || result;
      setSelectedConvo(detail);
      setNotes(detail.adminNotes || '');
      if (isMobile) setMobileDetailOpen(true);
      setSearchParams(prev => { prev.set('phone', phone); return prev; }, { replace: true });
      Promise.all([
        apiService.getConversationLabels(phone).then(r => setLabels((r.data?.labels || []).map((l: any) => l.name || l))).catch(() => setLabels([])),
        apiService.getConversationActions(phone).then(r => setActions(r.data || [])).catch(() => setActions([])),
        apiService.lookupClientByPhone(phone).then(r => { if (r?.data) setLinkedClient(r.data); }).catch(() => {}),
      ]);
    } catch (e: any) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    } finally {
      setLoadingDetail(false);
    }
  }, [isMobile, setSearchParams]);

  // Deep link: ?phone=xxx auto-selects conversation on mount
  const deepLinkHandled = useRef(false);
  useEffect(() => {
    if (deepLinkHandled.current) return;
    const phoneParam = searchParams.get('phone');
    if (phoneParam && !loading) {
      deepLinkHandled.current = true;
      handleSelectConvo(phoneParam);
    }
  }, [searchParams, loading, handleSelectConvo]);

  useEffect(() => { if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth' }); }, [selectedConvo?.messages]);

  // -----------------------------------------------------------------------
  // Actions
  // -----------------------------------------------------------------------

  const handleSend = useCallback(async () => {
    if (!selectedConvo || !messageText.trim() || sending) return;
    const text = messageText.trim();
    setSending(true);
    try {
      const result = await apiService.sendBotMessage(selectedConvo.phoneNumber, text);
      if (result?.templateRequired) {
        setSnackbar({ open: true, message: '⏰ Ventana 24h expirada. Envía desde el teléfono o espera que el cliente escriba.', severity: 'error' });
        navigator.clipboard?.writeText(text).catch(() => {});
      } else {
        setMessageText('');
        // Optimista: se muestra de una. El timestamp va en SEGUNDOS Unix como el
        // resto del sistema; con Date.now() (milisegundos) formatTimestamp lo
        // multiplicaba por 1000 otra vez y salía una fecha absurda, además de que
        // el deduplicador del SSE no lo reconocía y el mensaje aparecía dos veces.
        const ahoraSeg = Math.floor(Date.now() / 1000);
        setSelectedConvo(prev => prev ? {
          ...prev,
          messages: [...(prev.messages || []), { body: text, sender: 'assistant', timestamp: ahoraSeg }],
          lastMessageAt: new Date().toISOString(),
        } : prev);
      }
    } catch (e: any) {
      const data = e?.response?.data;
      if (data?.templateRequired) {
        setSnackbar({ open: true, message: '⏰ Ventana 24h expirada. Envía desde el teléfono o espera que el cliente escriba.', severity: 'error' });
        navigator.clipboard?.writeText(text).catch(() => {});
      } else {
        setSnackbar({ open: true, message: e.message || 'Error al enviar', severity: 'error' });
      }
    } finally {
      setSending(false);
    }
  }, [selectedConvo, messageText, sending]);

  const handlePause = async () => {
    if (!selectedConvo) return;
    try {
      await apiService.pauseBot(selectedConvo.phoneNumber, pauseMinutes);
      setPauseDialog(false);
      setSnackbar({ open: true, message: pauseMinutes === 0 ? 'Bot pausado indefinidamente' : `Bot pausado ${pauseMinutes}min`, severity: 'success' });
      handleSelectConvo(selectedConvo.phoneNumber);
    } catch (e: any) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    }
  };

  const handleResume = async () => {
    if (!selectedConvo) return;
    try {
      await apiService.resumeBot(selectedConvo.phoneNumber);
      setSnackbar({ open: true, message: 'Bot reanudado', severity: 'success' });
      handleSelectConvo(selectedConvo.phoneNumber);
    } catch (e: any) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    }
  };

  const handleStatusChange = async (status: string) => {
    if (!selectedConvo) return;
    // "Agendado" abre el modal que obliga a poner una fecha tentativa
    // (crea la cita tentativa en el calendario y de paso cambia el estado).
    if (status === 'scheduled') {
      setTentativeName(selectedConvo.leadName || selectedConvo.contactName || '');
      setTentativeDate('');
      setTentativeTime('');
      setTentativeNote('');
      setTentativeOpen(true);
      return;
    }
    try {
      await apiService.updateConversationStatus(selectedConvo.phoneNumber, status);
      setSnackbar({ open: true, message: `Estado → ${status}`, severity: 'success' });
      handleSelectConvo(selectedConvo.phoneNumber);
      fetchConversations();
    } catch (e: any) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    }
  };

  const handleConfirmTentative = async () => {
    if (!selectedConvo || !tentativeDate) return;
    setTentativeSaving(true);
    try {
      // 1. Crear la cita tentativa en el calendario
      // Si el usuario editó/ingresó el nombre en el diálogo, persistirlo también en la
      // conversación para que quede consistente en el CRM (no solo en la cita).
      const finalName = (tentativeName || '').trim();
      if (finalName && finalName !== (selectedConvo.leadName || '')) {
        try { await apiService.patchConversation(selectedConvo.phoneNumber, { leadName: finalName }); } catch { /* no bloquea */ }
      }
      await apiService.createTentativeAppointment({
        leadName: finalName || selectedConvo.leadName || selectedConvo.contactName || '',
        leadPhone: selectedConvo.phoneNumber,
        scheduledDate: tentativeTime ? `${tentativeDate}T${tentativeTime}` : tentativeDate,
        timeSlot: tentativeTime || undefined,
        notes: tentativeNote || undefined,
      });
      // 2. Cambiar el estado del lead a Agendado
      await apiService.updateConversationStatus(selectedConvo.phoneNumber, 'scheduled');
      setTentativeOpen(false);
      setSnackbar({ open: true, message: 'Agendado — cita tentativa creada en el calendario', severity: 'success' });
      handleSelectConvo(selectedConvo.phoneNumber);
      fetchConversations();
    } catch (e: any) {
      setSnackbar({ open: true, message: e?.response?.data?.message || e.message, severity: 'error' });
    } finally {
      setTentativeSaving(false);
    }
  };

  const handleNotesChange = (value: string) => {
    setNotes(value);
    if (notesTimeout) clearTimeout(notesTimeout);
    setNotesTimeout(setTimeout(async () => {
      if (!selectedConvo) return;
      setNotesSaving(true);
      try {
        await apiService.updateConversationNotes(selectedConvo.phoneNumber, value);
      } catch {} // eslint-disable-line
      setNotesSaving(false);
    }, 1500));
  };

  const handleAddLabel = async (label: string) => {
    if (!selectedConvo) return;
    setLabelMenuAnchor(null);
    try {
      await apiService.setConversationLabel(selectedConvo.phoneNumber, label, 'add');
      setLabels(prev => [...prev.filter(l => l !== label), label]);
    } catch (e: any) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    }
  };

  const handleRemoveLabel = async (label: string) => {
    if (!selectedConvo) return;
    try {
      await apiService.setConversationLabel(selectedConvo.phoneNumber, label, 'remove');
      setLabels(prev => prev.filter(l => l !== label));
    } catch (e: any) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    }
  };

  const handleCreateAction = async () => {
    if (!selectedConvo || !newActionMessage || !newActionDate) return;
    try {
      await apiService.createConversationAction(selectedConvo.phoneNumber, {
        message: newActionMessage,
        scheduledFor: new Date(newActionDate).toISOString(),
        name: selectedConvo.contactName || selectedConvo.leadName || '',
      });
      setNewActionDialog(false);
      setNewActionMessage('');
      setNewActionDate('');
      const r = await apiService.getConversationActions(selectedConvo.phoneNumber);
      setActions(r.data || []);
      setSnackbar({ open: true, message: 'Seguimiento programado', severity: 'success' });
    } catch (e: any) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    }
  };

  const handleCancelAction = async (actionId: string) => {
    if (!selectedConvo) return;
    try {
      await apiService.cancelConversationAction(selectedConvo.phoneNumber, actionId);
      setActions(prev => prev.map(a => a.actionId === actionId ? { ...a, schedule: { ...a.schedule, status: 'cancelled' } } : a));
    } catch (e: any) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    }
  };

  // -----------------------------------------------------------------------
  // Search debounce
  // -----------------------------------------------------------------------

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (searchTimeout) clearTimeout(searchTimeout);
    setSearchTimeout(setTimeout(() => fetchConversations(), 400));
  };

  // -----------------------------------------------------------------------
  // Render: Conversation List
  // -----------------------------------------------------------------------

  const renderConversationList = () => (
    <Paper sx={{ height: { xs: 'calc(100dvh - 200px)', md: 'calc(100vh - 180px)' }, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="h6" sx={{ flex: 1, fontSize: '1.1rem' }}>CRM Chat</Typography>
          {botStatus && (
            <Chip size="small" label={botStatus.status === 'connected' ? 'Online' : 'Offline'}
              color={botStatus.status === 'connected' ? 'success' : 'error'} variant="outlined" />
          )}
          <IconButton size="small" onClick={() => { setLoading(true); fetchConversations(); }}><RefreshIcon fontSize="small" /></IconButton>
        </Box>
        <TextField size="small" fullWidth placeholder="Buscar nombre o teléfono..." value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
      </Box>
      <Tabs value={filterTab} onChange={(_, v) => setFilterTab(v)} variant="scrollable" scrollButtons="auto"
        sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5, fontSize: '0.75rem' } }}>
        <Tab label="Todos" />
        <Tab label="Agenda" />
        <Tab label="Rutas" />
        <Tab label="Activos" />
        <Tab label="Pausados" />
        <Tab label="Escalados" />
        <Tab label="Seguim." />
      </Tabs>
      {filterTab !== 1 && filterTab !== 2 && (
        <Box sx={{ display: 'flex', gap: 0.5, px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider' }}>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <Select value={filterStage} onChange={(e) => setFilterStage(e.target.value)} displayEmpty
              sx={{ fontSize: '0.75rem', height: 28, '& .MuiSelect-select': { py: 0.3 } }}>
              <MenuItem value=""><em>Etapa</em></MenuItem>
              {Object.entries(STAGE_LABELS).map(([key, val]) => (
                <MenuItem key={key} value={key}>{val.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <Select value={filterZone} onChange={(e) => setFilterZone(e.target.value)} displayEmpty
              sx={{ fontSize: '0.75rem', height: 28, '& .MuiSelect-select': { py: 0.3 } }}>
              <MenuItem value=""><em>Zona</em></MenuItem>
              <MenuItem value="Santo Domingo">Santo Domingo</MenuItem>
              <MenuItem value="Cibao">Cibao</MenuItem>
              <MenuItem value="Sur">Sur</MenuItem>
              <MenuItem value="Este">Este</MenuItem>
            </Select>
          </FormControl>
          {(filterStage || filterZone) && (
            <Chip size="small" label="Limpiar" onDelete={() => { setFilterStage(''); setFilterZone(''); }}
              sx={{ height: 28, fontSize: '0.7rem' }} />
          )}
        </Box>
      )}
      <List sx={{ flex: 1, overflow: 'auto', py: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>
        ) : filterTab === 1 && agendaData ? (
          <Box sx={{ p: 1.5 }}>
            {(() => {
              const now = new Date();
              const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
              const tomorrowStart = new Date(todayStart.getTime() + 86400000);
              const weekEnd = new Date(todayStart.getTime() + 7 * 86400000);
              const items = (agendaData.followUps || []).map((f: any) => ({
                phone: f.phoneNumber, name: f.contactName || f.leadName || '', zone: f.clientZone,
                note: f.pendingFollowUp, date: new Date(f.pendingFollowUpAt), type: 'followup' as const,
                status: f.status, lastMessageAt: f.lastMessageAt, botPaused: f.botPaused,
              // Descarta fechas inválidas o época Unix (pendingFollowUpAt null -> new Date(null) = 1970 -> "31/12" en AST)
              })).filter((i: any) => Number.isFinite(i.date.getTime()) && i.date.getTime() > 0);
              const overdue = items.filter((i: any) => i.date < todayStart);
              const today = items.filter((i: any) => i.date >= todayStart && i.date < tomorrowStart);
              const tomorrow = items.filter((i: any) => i.date >= tomorrowStart && i.date < new Date(tomorrowStart.getTime() + 86400000));
              const thisWeek = items.filter((i: any) => i.date >= new Date(tomorrowStart.getTime() + 86400000) && i.date < weekEnd);
              const later = items.filter((i: any) => i.date >= weekEnd);
              const sections = [
                { title: 'Vencidos', items: overdue, color: '#f44336' },
                { title: 'Hoy', items: today, color: '#ff9800' },
                { title: 'Mañana', items: tomorrow, color: '#2196f3' },
                { title: 'Esta semana', items: thisWeek, color: '#4caf50' },
                { title: 'Próximos', items: later, color: '#9e9e9e' },
              ].filter(s => s.items.length > 0);
              if (sections.length === 0) return <Typography sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>No hay seguimientos pendientes</Typography>;
              return sections.map(section => (
                <Box key={section.title} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: section.color }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>{section.title}</Typography>
                    <Chip size="small" label={section.items.length} sx={{ height: 18, fontSize: '0.65rem' }} />
                  </Box>
                  {section.items.map((item: any) => (
                    <Paper key={item.phone} variant="outlined" sx={{ p: 1, mb: 0.5, cursor: 'pointer', borderLeft: `3px solid ${section.color}`,
                      '&:hover': { bgcolor: 'action.hover' } }}
                      onClick={() => handleSelectConvo(item.phone)}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                            {formatPhone(item.phone)}
                          </Typography>
                          {item.name && <Typography variant="caption" color="text.secondary">{item.name}</Typography>}
                          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.7rem' }}>
                            {item.note}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right', ml: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.3 }}>
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', color: section.color, fontWeight: 600 }}>
                            {item.date.toLocaleDateString('es-DO', { day: '2-digit', month: '2-digit' })}
                          </Typography>
                          {item.zone && <Typography variant="caption" display="block" sx={{ fontSize: '0.65rem' }}>{item.zone}</Typography>}
                          <Tooltip title="Limpiar seguimiento">
                            <IconButton size="small" onClick={(e) => {
                              e.stopPropagation();
                              apiService.setConversationFollowUp(item.phone, null).then(() => {
                                setAgendaData((prev: any) => ({
                                  ...prev,
                                  followUps: (prev.followUps || []).filter((f: any) => f.phoneNumber !== item.phone),
                                }));
                                setSnackbar({ open: true, message: 'Seguimiento limpiado', severity: 'success' });
                              }).catch(() => setSnackbar({ open: true, message: 'Error al limpiar', severity: 'error' }));
                            }}>
                              <ClearAllIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              ));
            })()}
          </Box>
        ) : filterTab === 2 ? (
          <Box sx={{ p: 1.5 }}>
            {(() => {
              const ZONES = ['Cibao', 'Sur', 'Este'];
              // Mismos colores que la página de Rutas de Instalación (REGION_COLORS) para consistencia
              const ZONE_COLORS: Record<string, string> = { Cibao: '#2e7d32', Sur: '#ed6c02', Este: '#7b1fa2' };
              const grouped: Record<string, any[]> = {};
              ZONES.forEach(z => { grouped[z] = []; });
              (routeQueue || []).forEach((item: any) => {
                const z = item.routeZone || item.zone || 'Cibao';
                if (grouped[z]) grouped[z].push(item);
                else grouped[z] = [item];
              });
              const totalWaiting = (routeQueue || []).filter((q: any) => q.status === 'waiting').length;
              const totalConfirmed = (routeQueue || []).filter((q: any) => q.status === 'confirmed').length;
              return (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <RouteIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Cola de Rutas</Typography>
                    <Chip size="small" label={`${totalWaiting} en espera`} color="primary" sx={{ height: 20, fontSize: '0.65rem' }} />
                    {totalConfirmed > 0 && (
                      <Chip size="small" label={`${totalConfirmed} confirmado${totalConfirmed !== 1 ? 's' : ''}`} color="success" sx={{ height: 20, fontSize: '0.65rem' }} />
                    )}
                  </Box>
                  {ZONES.map(zone => {
                    const waitingItems = grouped[zone].filter((q: any) => q.status === 'waiting');
                    const confirmedItems = grouped[zone].filter((q: any) => q.status === 'confirmed');
                    const allItems = [...confirmedItems, ...waitingItems];
                    const color = ZONE_COLORS[zone];

                    const renderQueueItem = (item: any) => {
                      const isConfirmed = item.status === 'confirmed';
                      return (
                        <Paper key={item.phone} variant="outlined"
                          sx={{ p: 1, mb: 0.5, cursor: 'pointer',
                            borderLeft: `3px solid ${isConfirmed ? '#2e7d32' : color}`,
                            bgcolor: isConfirmed ? 'success.50' : 'transparent',
                            '&:hover': { bgcolor: isConfirmed ? 'success.100' : 'action.hover' },
                          }}
                          onClick={() => handleSelectConvo(item.phone)}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {isConfirmed && <CheckIcon sx={{ fontSize: 14, color: 'success.main' }} />}
                                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                                  {item.name || formatPhone(item.phone)}
                                </Typography>
                              </Box>
                              {item.name && <Typography variant="caption" color="text.secondary">{formatPhone(item.phone)}</Typography>}
                              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.3, flexWrap: 'wrap' }}>
                                {isConfirmed && <Chip size="small" label="Confirmado" color="success" sx={{ height: 18, fontSize: '0.6rem' }} />}
                                {(item.confirmDate || item.preferredDay) && (
                                  <Chip size="small" label={item.confirmDate ? new Date(item.confirmDate + 'T12:00').toLocaleDateString('es-DO', { weekday: 'short', day: 'numeric', month: 'short' }) : item.preferredDay}
                                    sx={{ height: 18, fontSize: '0.6rem', bgcolor: '#e3f2fd' }}
                                    icon={<EventIcon sx={{ fontSize: 12 }} />} />
                                )}
                                {(item.timePreference || item.preferredTime) && (
                                  <Chip size="small" label={item.timePreference || item.preferredTime} sx={{ height: 18, fontSize: '0.6rem', bgcolor: '#fff3e0' }}
                                    icon={<ScheduleIcon sx={{ fontSize: 12 }} />} />
                                )}
                              </Box>
                            </Box>
                            <Box sx={{ textAlign: 'right', ml: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.3 }}>
                              <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                                {item.addedAt ? timeAgo(item.addedAt) : ''}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.3 }}>
                                {!isConfirmed && (
                                  <Tooltip title="Confirmar">
                                    <IconButton size="small" onClick={(e) => {
                                      e.stopPropagation();
                                      apiService.confirmRouteQueueClient(item.phone, {
                                        date: routeQueueConfig[zone]?.tentativeDate || undefined,
                                        timePreference: item.preferredTime || undefined,
                                      }).then(() => {
                                        setRouteQueue(prev => prev.map(q => q.phone === item.phone ? { ...q, status: 'confirmed', confirmDate: routeQueueConfig[zone]?.tentativeDate } : q));
                                        setSnackbar({ open: true, message: `${item.name || item.phone} confirmado`, severity: 'success' });
                                      }).catch(() => setSnackbar({ open: true, message: 'Error al confirmar', severity: 'error' }));
                                    }}>
                                      <CheckIcon sx={{ fontSize: 14, color: 'success.main' }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {isConfirmed && (
                                  <Tooltip title="Volver a espera">
                                    <IconButton size="small" onClick={(e) => {
                                      e.stopPropagation();
                                      apiService.revertRouteQueueClient(item.phone).then(() => {
                                        setRouteQueue(prev => prev.map(q => q.phone === item.phone ? { ...q, status: 'waiting', confirmDate: undefined, timePreference: undefined } : q));
                                        setSnackbar({ open: true, message: `${item.name || item.phone} vuelto a espera`, severity: 'success' });
                                      }).catch(() => setSnackbar({ open: true, message: 'Error al revertir', severity: 'error' }));
                                    }}>
                                      <UndoIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <Tooltip title="Eliminar de cola">
                                  <IconButton size="small" onClick={(e) => {
                                    e.stopPropagation();
                                    apiService.removeFromRouteQueue(item.phone).then(() => {
                                      setRouteQueue(prev => prev.filter(q => q.phone !== item.phone));
                                      setSnackbar({ open: true, message: 'Removido de la cola', severity: 'success' });
                                    }).catch(() => setSnackbar({ open: true, message: 'Error al remover', severity: 'error' }));
                                  }}>
                                    <DeleteIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                          </Box>
                        </Paper>
                      );
                    };

                    return (
                      <Box key={zone} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>{zone}</Typography>
                            <Chip size="small" label={`${allItems.length} persona${allItems.length !== 1 ? 's' : ''}`}
                              icon={<GroupsIcon sx={{ fontSize: 14 }} />}
                              sx={{ height: 20, fontSize: '0.65rem' }} />
                            {confirmedItems.length > 0 && (
                              <Chip size="small" label={`${confirmedItems.length} ✓`} color="success" variant="outlined"
                                sx={{ height: 20, fontSize: '0.65rem' }} />
                            )}
                          </Box>
                          {allItems.length >= 3 && (
                            <Chip size="small" label="Confirmar Ruta" color="success" variant="outlined"
                              icon={<CheckIcon sx={{ fontSize: 14 }} />}
                              sx={{ height: 24, fontSize: '0.7rem', cursor: 'pointer', fontWeight: 600 }}
                              onClick={() => setConfirmRouteDialog({ open: true, zone })} />
                          )}
                        </Box>
                        {allItems.length > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, ml: 2.5 }}>
                            <Typography variant="caption" color="text.secondary">Fecha:</Typography>
                            <input type="date" value={routeQueueConfig[zone]?.tentativeDate || ''}
                              onChange={async (e) => {
                                const val = e.target.value || null;
                                setRouteQueueConfig(prev => ({ ...prev, [zone]: { ...prev[zone], tentativeDate: val || undefined } }));
                                try { await apiService.setRouteQueueConfig(zone, val); } catch {}
                              }}
                              style={{ fontSize: '0.75rem', border: '1px solid #ccc', borderRadius: 4, padding: '2px 4px' }}
                            />
                            {routeQueueConfig[zone]?.tentativeDate && (
                              <Typography variant="caption" sx={{ fontWeight: 600, color: '#ed6c02' }}>
                                {new Date(routeQueueConfig[zone].tentativeDate + 'T12:00').toLocaleDateString('es-DO', { weekday: 'short', day: 'numeric', month: 'short' })}
                              </Typography>
                            )}
                          </Box>
                        )}
                        {allItems.length === 0 ? (
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 2.5 }}>Sin clientes en espera</Typography>
                        ) : (
                          <>
                            {confirmedItems.length > 0 && (
                              <Typography variant="caption" sx={{ ml: 2.5, fontWeight: 600, color: 'success.main', display: 'block', mb: 0.3 }}>
                                Confirmados ({confirmedItems.length})
                              </Typography>
                            )}
                            {confirmedItems.map(renderQueueItem)}
                            {waitingItems.length > 0 && confirmedItems.length > 0 && (
                              <Typography variant="caption" sx={{ ml: 2.5, fontWeight: 600, color: 'text.secondary', display: 'block', mt: 0.5, mb: 0.3 }}>
                                En espera ({waitingItems.length})
                              </Typography>
                            )}
                            {waitingItems.map(renderQueueItem)}
                          </>
                        )}
                        {allItems.length > 0 && allItems.length < 3 && (
                          <Typography variant="caption" sx={{ ml: 2.5, color: '#ff9800', fontStyle: 'italic' }}>
                            Faltan {3 - allItems.length} persona(s) para confirmar la ruta
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </>
              );
            })()}
          </Box>
        ) : conversations.length === 0 ? (
          <Typography sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>Sin conversaciones</Typography>
        ) : conversations.map((c) => (
          <ListItemButton key={c.phoneNumber} onClick={() => handleSelectConvo(c.phoneNumber)}
            selected={selectedConvo?.phoneNumber === c.phoneNumber}
            sx={{ py: 1, px: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <ListItemAvatar sx={{ minWidth: 44 }}>
              <Badge overlap="circular" variant="dot" invisible={c.lastSender !== 'client'}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                sx={{ '& .MuiBadge-dot': { bgcolor: '#4caf50', width: 10, height: 10, borderRadius: '50%' } }}>
                <Avatar sx={{ width: 36, height: 36, fontSize: '0.8rem', bgcolor: c.moneyMentioned ? '#4caf50' : '#1976d2' }}>
                  {getInitials(c.contactName || c.leadName)}
                </Avatar>
              </Badge>
            </ListItemAvatar>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" noWrap sx={{ fontWeight: c.lastSender === 'client' ? 700 : 400, maxWidth: 140 }}>
                    {formatPhone(c.phoneNumber)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{c.lastMessageAt ? timeAgo(c.lastMessageAt) : ''}</Typography>
                </Box>
              }
              secondary={
                <Box>
                  {(c.contactName || c.leadName) && (
                    <Typography variant="caption" noWrap sx={{ display: 'block', color: 'text.primary', fontWeight: 500, maxWidth: 200 }}>
                      {c.contactName || c.leadName}
                    </Typography>
                  )}
                  <Typography variant="caption" noWrap sx={{ display: 'block', color: 'text.secondary', maxWidth: 200 }}>
                    {c.lastSender === 'bot' && '🤖 '}{c.lastSender === 'operator' && '👤 '}{c.lastBody?.substring(0, 50) || ''}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.3, flexWrap: 'wrap' }}>
                    {c.flowStage && STAGE_LABELS[c.flowStage] && c.flowStage !== 'new' && (
                      <Chip size="small" label={STAGE_LABELS[c.flowStage].label} color={STAGE_LABELS[c.flowStage].color} sx={{ height: 18, fontSize: '0.65rem' }} />
                    )}
                    {c.clientZone && <Chip size="small" label={c.clientZone} sx={{ height: 18, fontSize: '0.65rem' }} icon={<LocationIcon sx={{ fontSize: 12 }} />} />}
                    {c.botPaused && <Chip size="small" label="Pausado" color="warning" sx={{ height: 18, fontSize: '0.65rem' }} />}
                    {c.pendingFollowUp && <Chip size="small" label={followUpLabel(c.pendingFollowUpAt)} color="secondary" sx={{ height: 18, fontSize: '0.65rem' }} icon={<FollowUpIcon sx={{ fontSize: 12 }} />} />}
                    {c.moneyMentioned && <Chip size="small" label="$$$" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#e8f5e9' }} />}
                  </Box>
                </Box>
              }
            />
          </ListItemButton>
        ))}
      </List>
    </Paper>
  );

  // -----------------------------------------------------------------------
  // Render: Chat Panel
  // -----------------------------------------------------------------------

  // `llenar` = el panel ocupa el alto que le da su contenedor en vez de calcularlo.
  // En el diálogo de móvil hace falta: ahí arriba hay una barra propia, así que un
  // alto fijo de 100vh-180px dejaba el campo de escribir fuera de la pantalla.
  const renderChatPanel = (llenar = false) => {
    const altoPanel = llenar
      ? { flex: 1, minHeight: 0 }
      // 100dvh y no 100vh: en el navegador del teléfono la barra de direcciones se
      // encoge y se estira, y con 100vh el input quedaba tapado por ella.
      : { height: { xs: 'calc(100dvh - 180px)', md: 'calc(100vh - 180px)' } };

    if (!selectedConvo) return (
      <Paper sx={{ ...altoPanel, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="text.secondary">Selecciona una conversación</Typography>
      </Paper>
    );

    const horasCliente = horasDesdeElCliente(selectedConvo);
    const windowExpired = isWindowExpired(selectedConvo);
    const horasQueQuedan = horasCliente === null ? null : Math.max(0, 24 - horasCliente);
    // Solo tiene enlace de WhatsApp quien tiene número de verdad: los clientes sin número
    // viven con una identidad `DO.xxx` que wa.me no entiende.
    const telWa = /^\d{10,15}$/.test(String(selectedConvo.phoneNumber || '')) ? selectedConvo.phoneNumber : null;

    return (
      <Paper sx={{ ...altoPanel, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ width: 32, height: 32, fontSize: '0.75rem', bgcolor: '#1976d2' }}>
            {getInitials(selectedConvo.contactName || selectedConvo.leadName)}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" noWrap>{selectedConvo.contactName || selectedConvo.leadName || formatPhone(selectedConvo.phoneNumber)}</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">{formatPhone(selectedConvo.phoneNumber)}</Typography>
              {windowExpired
                ? <Chip size="small" label="ventana cerrada" color="warning" sx={{ height: 16, fontSize: '0.6rem' }} />
                : horasQueQuedan !== null && horasQueQuedan < 6
                  ? <Chip size="small" label={`quedan ${horasQueQuedan.toFixed(0)}h`} color="error" sx={{ height: 16, fontSize: '0.6rem' }} />
                  : null}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {selectedConvo.botPaused ? (
              <Tooltip title="Reanudar bot"><IconButton size="small" color="success" onClick={handleResume}><ResumeIcon fontSize="small" /></IconButton></Tooltip>
            ) : (
              <Tooltip title="Pausar bot"><IconButton size="small" color="warning" onClick={() => setPauseDialog(true)}><PauseIcon fontSize="small" /></IconButton></Tooltip>
            )}
            {/* Sin número no hay a quién llamar: el botón se deshabilita en vez de
                abrir el marcador con una cadena que no es un teléfono. */}
            <Tooltip title={esBsuid(selectedConvo.phoneNumber) ? 'Este cliente no comparte su número' : 'Llamar'}>
              <span>
                <IconButton
                  size="small"
                  disabled={esBsuid(selectedConvo.phoneNumber)}
                  component="a"
                  href={esBsuid(selectedConvo.phoneNumber) ? undefined : `tel:+${selectedConvo.phoneNumber}`}
                >
                  <PhoneIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {/* Messages */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 1.5, bgcolor: '#f5f5f5', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {loadingDetail ? <CircularProgress sx={{ m: 'auto' }} /> :
            (selectedConvo.messages || []).map((msg, i) => {
              const isClient = msg.sender === 'client';
              const isBot = msg.sender === 'bot';
              const isOperator = msg.sender === 'operator';
              const bgColor = isClient ? '#fff' : isBot ? '#e3f2fd' : isOperator ? '#fff3e0' : '#e8f5e9';
              const align = isClient ? 'flex-start' : 'flex-end';
              const senderLabel = isBot ? 'Sofia' : isOperator ? 'WhatsApp' : msg.sender === 'assistant' ? 'Portal' : '';

              return (
                <Box key={i} sx={{ display: 'flex', justifyContent: align }}>
                  <Box sx={{ maxWidth: '75%', bgcolor: bgColor, borderRadius: 2, px: 1.5, py: 0.8, boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>
                    {senderLabel && <Typography variant="caption" sx={{ fontWeight: 600, color: isBot ? '#1565c0' : isOperator ? '#e65100' : '#2e7d32', display: 'block', fontSize: '0.65rem' }}>{senderLabel}</Typography>}
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.85rem' }}>{msg.body}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', textAlign: 'right', mt: 0.3, fontSize: '0.65rem' }}>
                      {formatTimestamp(msg.timestamp)}
                    </Typography>
                  </Box>
                </Box>
              );
            })
          }
          <div ref={chatEndRef} />
        </Box>

        {/* Send bar */}
        {windowExpired && (
          <Alert
            severity="warning"
            sx={{ mx: 1, mt: 1, py: 0, '& .MuiAlert-message': { fontSize: '0.75rem' } }}
            action={telWa ? (
              <Button
                size="small" color="inherit"
                href={`https://wa.me/${telWa}`} target="_blank" rel="noopener"
              >
                Abrir WhatsApp
              </Button>
            ) : undefined}
          >
            {horasCliente === null
              ? 'Sin mensajes del cliente — desde aquí Meta no deja escribir.'
              : `El cliente escribió hace ${Math.round(horasCliente)}h: Meta ya no deja texto libre desde aquí. Escríbele desde el teléfono.`}
          </Alert>
        )}
        <Box sx={{
          p: 1, borderTop: '1px solid', borderColor: 'divider',
          display: 'flex', gap: 1, alignItems: 'flex-end', flexShrink: 0,
          bgcolor: 'background.paper',
          // Deja libre la barra de gestos del iPhone, que si no tapa el botón de enviar
          pb: 'calc(env(safe-area-inset-bottom, 0px) + 8px)',
        }}>
          <TextField size="small" fullWidth multiline maxRows={4}
            placeholder={windowExpired ? 'Ventana cerrada — escríbele desde el teléfono' : 'Escribe un mensaje...'}
            value={messageText} onChange={(e) => setMessageText(e.target.value)}
            disabled={windowExpired}
            // 16px evita que Safari haga zoom automático al enfocar el campo,
            // que descoloca toda la pantalla y obliga a volver a acomodarla.
            inputProps={{ style: { fontSize: 16 }, enterKeyHint: 'send' }}
            onKeyDown={(e) => {
              // En el teléfono Enter debe ser salto de línea, no enviar: el teclado
              // no tiene Shift cómodo y se enviaban mensajes a medias.
              if (e.key === 'Enter' && !e.shiftKey && !isMobile) { e.preventDefault(); handleSend(); }
            }}
          />
          <IconButton
            color="primary"
            onClick={handleSend}
            disabled={!messageText.trim() || sending || windowExpired}
            sx={{ mb: 0.25, ...(isMobile && { bgcolor: 'primary.main', color: 'primary.contrastText', '&:hover': { bgcolor: 'primary.dark' }, '&.Mui-disabled': { bgcolor: 'action.disabledBackground' } }) }}
            aria-label="Enviar mensaje"
          >
            {sending ? <CircularProgress size={20} /> : <SendIcon />}
          </IconButton>
        </Box>
      </Paper>
    );
  };

  // -----------------------------------------------------------------------
  // Render: Side Panel (Info / Seguimientos / Notas)
  // -----------------------------------------------------------------------

  const renderSidePanel = () => {
    if (!selectedConvo) return null;

    return (
      <Paper sx={{ height: 'calc(100vh - 180px)', display: 'flex', flexDirection: 'column' }}>
        <Tabs value={sideTab} onChange={(_, v) => setSideTab(v)} variant="fullWidth"
          sx={{ minHeight: 36, borderBottom: '1px solid', borderColor: 'divider', '& .MuiTab-root': { minHeight: 36, py: 0.5, fontSize: '0.7rem' } }}>
          <Tab icon={<InfoIcon sx={{ fontSize: 16 }} />} label="Info" iconPosition="start" />
          <Tab icon={<ScheduleIcon sx={{ fontSize: 16 }} />} label="Seguim." iconPosition="start" />
          <Tab icon={<NoteIcon sx={{ fontSize: 16 }} />} label="Notas" iconPosition="start" />
        </Tabs>

        <Box sx={{ flex: 1, overflow: 'auto', p: 1.5 }}>
          {/* TAB: Info */}
          {sideTab === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {/* Lo que dejó el clasificador diario (backend/cron/leadClassifier.js):
                  resumen de la conversación, nivel de interés y el freno principal.
                  Va arriba para leerlo antes de contestar. */}
              {(selectedConvo as any).clasificacion?.resumen && (
                <Box sx={{ p: 1, borderRadius: 1, bgcolor: 'action.hover' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                    {(selectedConvo as any).clasificacion?.interes && (
                      <Box sx={{
                        width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                        bgcolor: (selectedConvo as any).clasificacion.interes === 'alto' ? '#2e7d32'
                          : (selectedConvo as any).clasificacion.interes === 'medio' ? '#ed6c02' : '#9e9e9e',
                      }} />
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      En qué quedó{(selectedConvo as any).clasificacion?.interes ? ` · interés ${(selectedConvo as any).clasificacion.interes}` : ''}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                    {(selectedConvo as any).clasificacion.resumen}
                  </Typography>
                  {(selectedConvo as any).clasificacion?.objecion && (
                    <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.5 }}>
                      Freno: {(selectedConvo as any).clasificacion.objecion}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Status */}
              <FormControl size="small" fullWidth>
                <InputLabel>Estado</InputLabel>
                <Select value={selectedConvo.status || 'active'} label="Estado"
                  onChange={(e) => handleStatusChange(e.target.value)}>
                  {STATUS_OPTIONS.map(s => <MenuItem key={s.value} value={s.value}><Chip size="small" label={s.label} sx={{ bgcolor: s.color, color: '#fff', height: 20 }} /></MenuItem>)}
                </Select>
              </FormControl>

              {/* Flow stage */}
              <FormControl size="small" fullWidth>
                <Typography variant="caption" color="text.secondary">Etapa</Typography>
                <Select
                  value={selectedConvo.flowStage || 'new'}
                  onChange={async (e) => {
                    const stage = e.target.value;
                    try {
                      await apiService.setFlowStage(selectedConvo.phoneNumber, stage);
                      setSelectedConvo({ ...selectedConvo, flowStage: stage });
                      setConversations(prev => prev.map(c => c.phoneNumber === selectedConvo.phoneNumber ? { ...c, flowStage: stage } : c));
                    } catch {}
                  }}
                  size="small"
                >
                  {Object.entries(STAGE_LABELS).map(([key, val]) => (
                    <MenuItem key={key} value={key}><Chip size="small" label={val.label} color={val.color} sx={{ height: 20 }} /></MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Contact info — editable */}
              <Divider />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Contacto</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <TextField size="small" variant="standard" placeholder="Nombre"
                  value={selectedConvo.leadName || selectedConvo.contactName || ''}
                  onChange={(e) => setSelectedConvo({ ...selectedConvo, leadName: e.target.value })}
                  onBlur={async (e) => {
                    const val = e.target.value.trim();
                    if (val && val !== selectedConvo.contactName) {
                      await apiService.patchConversation(selectedConvo.phoneNumber, { leadName: val }).catch(() => {});
                      setConversations(prev => prev.map(c => c.phoneNumber === selectedConvo.phoneNumber ? { ...c, leadName: val } : c));
                    }
                  }}
                  InputProps={{ sx: { fontSize: '0.85rem' }, startAdornment: <InputAdornment position="start"><PersonIcon sx={{ fontSize: 16 }} /></InputAdornment> }}
                />
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} /> {formatPhone(selectedConvo.phoneNumber)}
                  <Tooltip title="Copiar">
                    <IconButton size="small" onClick={() => { navigator.clipboard?.writeText(selectedConvo.phoneNumber); setSnackbar({ open: true, message: 'Copiado', severity: 'success' }); }}>
                      <CopyIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                  </Tooltip>
                </Typography>
                <TextField size="small" variant="standard" placeholder="Zona / Ubicación"
                  value={selectedConvo.clientZone || ''}
                  onChange={(e) => setSelectedConvo({ ...selectedConvo, clientZone: e.target.value })}
                  onBlur={async (e) => {
                    const val = e.target.value.trim();
                    await apiService.patchConversation(selectedConvo.phoneNumber, { clientZone: val }).catch(() => {});
                    setConversations(prev => prev.map(c => c.phoneNumber === selectedConvo.phoneNumber ? { ...c, clientZone: val } : c));
                  }}
                  InputProps={{ sx: { fontSize: '0.85rem' }, startAdornment: <InputAdornment position="start"><LocationIcon sx={{ fontSize: 16 }} /></InputAdornment> }}
                />
                {selectedConvo.clientLocation && <Typography variant="caption" color="text.secondary" sx={{ ml: 3 }}>{selectedConvo.clientLocation}</Typography>}
                {selectedConvo.leadVehicleCount > 0 && <Typography variant="body2"><CarIcon sx={{ fontSize: 16, verticalAlign: 'text-bottom', mr: 0.5 }} />{selectedConvo.leadVehicleCount} vehículo(s)</Typography>}
                {selectedConvo.leadBusinessType && <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{selectedConvo.leadBusinessType}</Typography>}
                {selectedConvo.adHeadline && <Typography variant="caption" color="text.secondary">Ad: {selectedConvo.adHeadline}</Typography>}
              </Box>

              {/* Sacar del tablero: lo quita del Kanban y del tablero de Leads sin
                  borrar la conversación (kanbanArchived). Para limpiar el pipeline
                  desde el mismo chat, sin tener que volver al tablero. */}
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                startIcon={<ArchiveIcon sx={{ fontSize: 16 }} />}
                onClick={async () => {
                  try {
                    await apiService.bulkConversations([selectedConvo.phoneNumber], 'archive', { archived: true });
                    setConversations(prev => prev.filter(c => c.phoneNumber !== selectedConvo.phoneNumber));
                    setSnackbar({ open: true, message: 'Lead sacado del tablero', severity: 'success' });
                  } catch {
                    setSnackbar({ open: true, message: 'No se pudo sacar del tablero', severity: 'error' });
                  }
                }}
                sx={{ fontSize: '0.7rem' }}
              >
                Sacar del tablero
              </Button>

              {/* Linked client */}
              {linkedClient && (
                <>
                  <Divider />
                  <Paper variant="outlined" sx={{ p: 1, bgcolor: 'success.50', borderColor: 'success.200', cursor: 'pointer' }}
                    onClick={() => window.open(`/clients/${linkedClient._id}`, '_blank')}>
                    <Typography variant="caption" color="success.main" sx={{ fontWeight: 700, display: 'block' }}>Cliente registrado</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{linkedClient.name}</Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.3, flexWrap: 'wrap' }}>
                      <Chip size="small" label={linkedClient.status === 'active' ? 'Activo' : linkedClient.status} color={linkedClient.status === 'active' ? 'success' : 'default'} sx={{ height: 18, fontSize: '0.6rem' }} />
                      <Chip size="small" label={`${linkedClient.activeDevices}/${linkedClient.deviceCount} GPS`} sx={{ height: 18, fontSize: '0.6rem' }} />
                      {linkedClient.latestRenewal && (
                        <Chip size="small" label={`Renov: ${linkedClient.latestRenewal.status}`}
                          color={linkedClient.latestRenewal.status === 'paid' ? 'success' : linkedClient.latestRenewal.status === 'overdue' ? 'error' : 'default'}
                          sx={{ height: 18, fontSize: '0.6rem' }} />
                      )}
                    </Box>
                  </Paper>
                </>
              )}

              {/* Bot status */}
              <Divider />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Bot</Typography>
                <Typography variant="body2">
                  {selectedConvo.botPaused ? `⏸ Pausado (${selectedConvo.botPausedReason || 'manual'})` : '▶ Activo'}
                </Typography>
                {selectedConvo.botPaused && (
                  <Typography variant="caption" color="text.secondary">
                    {selectedConvo.botPausedUntil ? `Hasta: ${formatTimestamp(selectedConvo.botPausedUntil)}` : 'Indefinidamente'}
                  </Typography>
                )}
              </Box>

              {/* Route Queue */}
              <Divider />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Cola de Ruta</Typography>
                {selectedConvo.routeQueueStatus ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <Chip size="small" label={`${selectedConvo.routeQueueZone || '?'} — ${selectedConvo.routeQueueStatus === 'confirmed' ? 'Confirmado' : 'En espera'}`}
                      color={selectedConvo.routeQueueStatus === 'confirmed' ? 'success' : 'primary'} sx={{ height: 22, fontSize: '0.7rem' }}
                      icon={selectedConvo.routeQueueStatus === 'confirmed' ? <CheckIcon sx={{ fontSize: 14 }} /> : <RouteIcon sx={{ fontSize: 14 }} />} />
                    <Tooltip title="Eliminar de cola">
                      <IconButton size="small" onClick={async () => {
                        try {
                          await apiService.removeFromRouteQueue(selectedConvo.phoneNumber);
                          setSelectedConvo({ ...selectedConvo, routeQueueStatus: undefined, routeQueueZone: undefined });
                          setRouteQueue(prev => prev.filter(q => q.phone !== selectedConvo.phoneNumber.replace(/\D/g, '')));
                          setSnackbar({ open: true, message: 'Removido de la cola', severity: 'success' });
                        } catch { setSnackbar({ open: true, message: 'Error al remover', severity: 'error' }); }
                      }}>
                        <DeleteIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                    {['Cibao', 'Sur', 'Este'].map(z => (
                      <Chip key={z} size="small" label={z} variant="outlined" sx={{ cursor: 'pointer', fontSize: '0.7rem', height: 22 }}
                        icon={<AddToQueueIcon sx={{ fontSize: 14 }} />}
                        onClick={async () => {
                          try {
                            await apiService.addToRouteQueue(selectedConvo.phoneNumber, z, selectedConvo.leadName || selectedConvo.contactName);
                            setSelectedConvo({ ...selectedConvo, routeQueueStatus: 'waiting', routeQueueZone: z });
                            setRouteQueue(prev => [...prev, { phone: selectedConvo.phoneNumber.replace(/\D/g, ''), name: selectedConvo.leadName || selectedConvo.contactName || selectedConvo.phoneNumber, routeZone: z, status: 'waiting', addedAt: new Date().toISOString() }]);
                            setSnackbar({ open: true, message: `Agregado a cola ${z}`, severity: 'success' });
                          } catch (err: any) {
                            const msg = err.response?.data?.message || 'Error al agregar';
                            setSnackbar({ open: true, message: msg.includes('Already') ? 'Ya está en la cola' : msg, severity: 'error' });
                          }
                        }} />
                    ))}
                  </Box>
                )}
              </Box>

              {/* Labels */}
              <Divider />
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Etiquetas</Typography>
                  <IconButton size="small" onClick={(e) => setLabelMenuAnchor(e.currentTarget)}><AddIcon sx={{ fontSize: 16 }} /></IconButton>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                  {labels.length === 0 && <Typography variant="caption" color="text.secondary">Sin etiquetas</Typography>}
                  {labels.map(l => (
                    <Chip key={l} size="small" label={l} onDelete={() => handleRemoveLabel(l)} sx={{ height: 22, fontSize: '0.7rem' }} />
                  ))}
                </Box>
                <Menu anchorEl={labelMenuAnchor} open={!!labelMenuAnchor} onClose={() => setLabelMenuAnchor(null)}>
                  {LABEL_OPTIONS.filter(l => !labels.includes(l)).map(l => (
                    <MenuItem key={l} onClick={() => handleAddLabel(l)} sx={{ fontSize: '0.8rem' }}>{l}</MenuItem>
                  ))}
                </Menu>
              </Box>

              {/* Timestamps */}
              <Divider />
              <Box>
                <Typography variant="caption" color="text.secondary">Creado: {selectedConvo.createdAt ? formatTimestamp(selectedConvo.createdAt) : '—'}</Typography>
                <br />
                <Typography variant="caption" color="text.secondary">Último msg: {selectedConvo.lastMessageAt ? formatTimestamp(selectedConvo.lastMessageAt) : '—'}</Typography>
                <br />
                <Typography variant="caption" color="text.secondary">Mensajes: {selectedConvo.messagesCount || 0}</Typography>
              </Box>
            </Box>
          )}

          {/* TAB: Seguimientos */}
          {sideTab === 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {/* Current follow-up flag */}
              {selectedConvo.pendingFollowUp && (
                <Alert severity={followUpLabel(selectedConvo.pendingFollowUpAt) === 'Vencido' ? 'error' : 'info'}
                  sx={{ py: 0.5, '& .MuiAlert-message': { fontSize: '0.8rem' } }}
                  action={<IconButton size="small" onClick={async () => {
                    await apiService.setConversationFollowUp(selectedConvo.phoneNumber, null);
                    handleSelectConvo(selectedConvo.phoneNumber);
                  }}><CloseIcon fontSize="small" /></IconButton>}>
                  <strong>Pendiente:</strong> {selectedConvo.pendingFollowUp}
                  {selectedConvo.pendingFollowUpAt && <Typography variant="caption" display="block">{followUpLabel(selectedConvo.pendingFollowUpAt)}</Typography>}
                </Alert>
              )}

              {/* Quick follow-up scheduling */}
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Dar seguimiento</Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {[
                  { label: 'Hoy', hours: 2 },
                  { label: 'Mañana', hours: 24 },
                  { label: 'En 3 días', hours: 72 },
                  { label: 'En 1 semana', hours: 168 },
                ].map(opt => (
                  <Chip key={opt.label} size="small" label={opt.label} variant="outlined" sx={{ cursor: 'pointer', fontSize: '0.75rem' }}
                    icon={<ScheduleIcon sx={{ fontSize: 14 }} />}
                    onClick={async () => {
                      const followUpAt = new Date(Date.now() + opt.hours * 3600000).toISOString();
                      await apiService.setConversationFollowUp(selectedConvo.phoneNumber, `Seguimiento - ${opt.label}`, followUpAt);
                      handleSelectConvo(selectedConvo.phoneNumber);
                      fetchConversations();
                      setSnackbar({ open: true, message: `Seguimiento: ${opt.label}`, severity: 'success' });
                    }} />
                ))}
              </Box>
              <TextField label="Fecha específica" type="datetime-local" size="small" fullWidth
                InputLabelProps={{ shrink: true }}
                onChange={async (e) => {
                  if (!e.target.value) return;
                  const followUpAt = new Date(e.target.value).toISOString();
                  await apiService.setConversationFollowUp(selectedConvo.phoneNumber, 'Seguimiento programado', followUpAt);
                  handleSelectConvo(selectedConvo.phoneNumber);
                  fetchConversations();
                  setSnackbar({ open: true, message: 'Seguimiento programado', severity: 'success' });
                }} />

              <Divider />

              {/* Create action button */}
              <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setNewActionDialog(true)}>
                Programar mensaje automático
              </Button>

              {/* Scheduled actions list */}
              {actions.length > 0 && (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Mensajes programados</Typography>
                  {actions.map(a => (
                    <Paper key={a.actionId} variant="outlined" sx={{ p: 1, borderLeft: `3px solid ${a.schedule.status === 'pending' ? '#ff9800' : a.schedule.status === 'sent' ? '#4caf50' : '#9e9e9e'}` }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            {a.schedule.status === 'pending' ? '⏳' : a.schedule.status === 'sent' ? '✅' : '❌'} {a.type === 'reminder' ? 'Recordatorio' : 'Follow-up'}
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem', mt: 0.3 }}>{a.action.message}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            📅 {formatTimestamp(a.schedule.scheduledFor)} · por {a.createdBy}
                          </Typography>
                        </Box>
                        {a.schedule.status === 'pending' && (
                          <IconButton size="small" onClick={() => handleCancelAction(a.actionId)} color="error">
                            <CancelIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        )}
                      </Box>
                    </Paper>
                  ))}
                </>
              )}
            </Box>
          )}

          {/* TAB: Notas */}
          {sideTab === 2 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Notas del cliente</Typography>
                {notesSaving && <CircularProgress size={14} />}
              </Box>
              <TextField multiline minRows={6} maxRows={15} fullWidth size="small" placeholder="Escribe notas sobre este cliente... (se guarda automáticamente)"
                value={notes} onChange={(e) => handleNotesChange(e.target.value)}
                sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
              />
              <Divider />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Notas rápidas</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {['Llamar mañana', 'Esperando pago', 'Coordinar instalación', 'No contestó', 'Enviar ubicación', 'Confirmar hora'].map(q => (
                  <Chip key={q} size="small" label={q} onClick={() => handleNotesChange(notes ? `${notes}\n• ${q}` : `• ${q}`)}
                    sx={{ height: 24, fontSize: '0.7rem', cursor: 'pointer' }} variant="outlined" />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Paper>
    );
  };

  // -----------------------------------------------------------------------
  // Main Render
  // -----------------------------------------------------------------------

  return (
    <Box sx={{ p: isMobile ? 1 : 2 }}>
      {/* Toggle de vista: Lista (chat) vs Tablero (Kanban) */}
      <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
        <Button
          size="small" startIcon={<ListIcon />}
          variant={viewMode === 'list' ? 'contained' : 'outlined'}
          onClick={() => setViewMode('list')}
        >Lista</Button>
        <Button
          size="small" startIcon={<KanbanIcon />}
          variant={viewMode === 'board' ? 'contained' : 'outlined'}
          onClick={() => setViewMode('board')}
        >Tablero</Button>

        <Box sx={{ flex: 1 }} />

        {/* Los navegadores del teléfono solo dan el permiso si sale de un toque */}
        {permisoAviso !== 'granted' && permisoAviso !== 'unsupported' && (
          <Tooltip title="Activar avisos de mensajes nuevos">
            <Button size="small" variant="outlined" startIcon={<NotifIcon />} onClick={pedirPermisoAviso}>
              Avisos
            </Button>
          </Tooltip>
        )}

        {/* Interruptor global: apaga solo las respuestas automáticas */}
        <Tooltip title={globalPaused
          ? 'El bot no está respondiendo. Los follow-ups y avisos de ruta siguen activos.'
          : 'Apagar las respuestas automáticas del bot'}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <BotIcon fontSize="small" color={globalPaused ? 'disabled' : 'success'} />
            <Typography variant="caption" sx={{ color: globalPaused ? 'error.main' : 'text.secondary', fontWeight: globalPaused ? 700 : 400 }}>
              {globalPaused ? 'Bot apagado' : 'Bot activo'}
            </Typography>
            <Switch
              size="small"
              color="error"
              checked={globalPaused}
              disabled={globalPauseBusy}
              onChange={(e) => {
                if (e.target.checked) setConfirmGlobalPause(true);
                else toggleGlobalPause(false);
              }}
            />
          </Box>
        </Tooltip>
      </Box>

      {globalPaused && (
        <Alert severity="warning" sx={{ mb: 1.5 }}
          action={<Button size="small" color="inherit" disabled={globalPauseBusy} onClick={() => toggleGlobalPause(false)}>Encender</Button>}>
          <strong>El bot está apagado</strong> — no responde mensajes entrantes
          {globalPauseBy ? ` (lo apagó ${globalPauseBy})` : ''}. Los mensajes se siguen
          guardando y puedes contestar tú desde aquí. Los follow-ups a clientes y los
          avisos de ruta no se ven afectados.
        </Alert>
      )}

      <Dialog open={confirmGlobalPause} onClose={() => setConfirmGlobalPause(false)}>
        <DialogTitle>¿Apagar el bot?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Dejará de responder automáticamente a <strong>todos</strong> los clientes.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Los mensajes se siguen recibiendo y guardando, y tú puedes contestar desde el
            portal. Los follow-ups a clientes, los avisos de ruta y los recordatorios
            siguen funcionando igual.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmGlobalPause(false)}>Cancelar</Button>
          <Button color="error" variant="contained" disabled={globalPauseBusy} onClick={() => toggleGlobalPause(true)}>
            Apagar el bot
          </Button>
        </DialogActions>
      </Dialog>

      {viewMode === 'board' ? (
        <LeadsKanbanBoard />
      ) : (
      <Grid container spacing={1.5}>
        {/* List Panel */}
        <Grid size={{ xs: 12, md: 3 }}>
          {renderConversationList()}
        </Grid>

        {/* Chat Panel — inline solo en desktop; en móvil se usa el diálogo full-screen */}
        {!isMobile && (
          <Grid size={{ md: selectedConvo ? 5 : 9 }}>
            {renderChatPanel()}
          </Grid>
        )}

        {/* Side Panel */}
        {selectedConvo && !isMobile && (
          <Grid size={{ md: 4 }}>
            {renderSidePanel()}
          </Grid>
        )}
      </Grid>
      )}

      {/* Mobile: Full-screen chat dialog */}
      {isMobile && (
        <Dialog
          fullScreen
          open={mobileDetailOpen}
          onClose={() => setMobileDetailOpen(false)}
          // dvh sigue a la barra del navegador cuando se encoge; con vh el input
          // de escribir quedaba debajo del borde y había que hacer scroll para verlo.
          PaperProps={{ sx: { height: '100dvh', maxHeight: '100dvh', overflow: 'hidden' } }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            {/* Barra fija: volver, con quién hablas, y la ficha del lead */}
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.75,
              borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0,
              // Respeta la muesca / barra superior del iPhone
              pt: 'calc(env(safe-area-inset-top, 0px) + 6px)',
              bgcolor: 'background.paper',
            }}>
              <IconButton edge="start" onClick={() => setMobileDetailOpen(false)} aria-label="Volver a la lista">
                <BackIcon />
              </IconButton>
              <Typography variant="subtitle1" sx={{ flex: 1, minWidth: 0, fontWeight: 600 }} noWrap>
                {selectedConvo?.contactName || selectedConvo?.leadName || formatPhone(selectedConvo?.phoneNumber || '')}
              </Typography>
              <Button
                size="small"
                variant={mobileInfoOpen ? 'contained' : 'outlined'}
                startIcon={<InfoIcon />}
                onClick={() => setMobileInfoOpen(v => !v)}
                sx={{ flexShrink: 0 }}
              >
                Ficha
              </Button>
            </Box>

            {/* La ficha se despliega arriba en vez de quedar apilada debajo del chat,
                que era lo que empujaba el campo de escribir fuera de la pantalla. */}
            {mobileInfoOpen && (
              <Box sx={{ maxHeight: '45dvh', overflow: 'auto', borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
                {renderSidePanel()}
              </Box>
            )}

            {renderChatPanel(true)}
          </Box>
        </Dialog>
      )}

      {/* Pause dialog */}
      <Dialog open={pauseDialog} onClose={() => setPauseDialog(false)} maxWidth="xs">
        <DialogTitle>Pausar Bot</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
            {[15, 30, 60, 120, 480].map(m => (
              <Chip key={m} label={m >= 60 ? `${m / 60}h` : `${m}min`} onClick={() => setPauseMinutes(m)}
                color={pauseMinutes === m ? 'primary' : 'default'} variant={pauseMinutes === m ? 'filled' : 'outlined'} />
            ))}
            <Chip label="Indefinido" onClick={() => setPauseMinutes(0)}
              color={pauseMinutes === 0 ? 'error' : 'default'} variant={pauseMinutes === 0 ? 'filled' : 'outlined'} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPauseDialog(false)}>Cancelar</Button>
          <Button onClick={handlePause} variant="contained" color={pauseMinutes === 0 ? 'error' : 'primary'}>
            {pauseMinutes === 0 ? 'Pausar Indefinidamente' : 'Pausar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* New action dialog */}
      <Dialog open={newActionDialog} onClose={() => setNewActionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Programar Mensaje</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Mensaje" multiline rows={3} fullWidth value={newActionMessage}
              onChange={(e) => setNewActionMessage(e.target.value)} placeholder="Escribe el mensaje que se enviará..." />
            <TextField label="Fecha y hora" type="datetime-local" fullWidth value={newActionDate}
              onChange={(e) => setNewActionDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewActionDialog(false)}>Cancelar</Button>
          <Button onClick={handleCreateAction} variant="contained" disabled={!newActionMessage || !newActionDate}>Programar</Button>
        </DialogActions>
      </Dialog>

      {/* Tentative appointment dialog (al marcar Agendado) */}
      <Dialog open={tentativeOpen} onClose={() => setTentativeOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          📅 Agendar (tentativa)
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Marca a <strong>{selectedConvo?.leadName || selectedConvo?.contactName || selectedConvo?.phoneNumber}</strong> como agendado y lo pone en el calendario como cita tentativa para que no se te pierda. Luego la confirmas con técnico y dispositivo.
            </Typography>
            <TextField
              label="Nombre del cliente" fullWidth
              value={tentativeName}
              onChange={(e) => setTentativeName(e.target.value)}
              placeholder="Nombre para la cita y la instalación"
              helperText="Se usa en la cita y se hereda al cerrar la instalación"
            />
            <TextField
              label="Fecha tentativa" type="date" fullWidth required
              value={tentativeDate}
              onChange={(e) => setTentativeDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: new Date().toISOString().split('T')[0] }}
            />
            <TextField
              label="Hora (opcional)" type="time" fullWidth
              value={tentativeTime}
              onChange={(e) => setTentativeTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Nota (opcional)" fullWidth multiline rows={2}
              value={tentativeNote}
              onChange={(e) => setTentativeNote(e.target.value)}
              placeholder="Ej: viernes nos vemos, mandará ubicación..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTentativeOpen(false)} disabled={tentativeSaving}>Cancelar</Button>
          <Button onClick={handleConfirmTentative} variant="contained" disabled={!tentativeDate || tentativeSaving}>
            {tentativeSaving ? 'Guardando...' : 'Agendar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm route dialog */}
      <Dialog open={confirmRouteDialog.open} onClose={() => setConfirmRouteDialog({ open: false, zone: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>Confirmar Ruta — {confirmRouteDialog.zone}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Se notificará a todos los clientes en espera de la zona {confirmRouteDialog.zone} que la ruta está confirmada.
            </Typography>
            <TextField label="Fecha de la ruta" type="date" fullWidth value={confirmRouteDate}
              onChange={(e) => setConfirmRouteDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField label="Mensaje personalizado (opcional)" multiline rows={3} fullWidth
              value={confirmRouteMessage} onChange={(e) => setConfirmRouteMessage(e.target.value)}
              placeholder={`Ej: La ruta del ${confirmRouteDialog.zone} está confirmada para este sábado. Te contactaremos para coordinar la hora.`} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setConfirmRouteDialog({ open: false, zone: '' }); setConfirmRouteDate(''); setConfirmRouteMessage(''); }}>Cancelar</Button>
          <Button variant="contained" color="success" disabled={!confirmRouteDate}
            onClick={async () => {
              try {
                await apiService.confirmRouteQueue(confirmRouteDialog.zone, confirmRouteDate, confirmRouteMessage || undefined);
                setSnackbar({ open: true, message: `Ruta ${confirmRouteDialog.zone} confirmada. Clientes notificados.`, severity: 'success' });
                setConfirmRouteDialog({ open: false, zone: '' });
                setConfirmRouteDate('');
                setConfirmRouteMessage('');
                fetchConversations();
              } catch (e: any) {
                setSnackbar({ open: true, message: e.message || 'Error al confirmar ruta', severity: 'error' });
              }
            }}>
            Confirmar y Notificar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default LeadsCRMPage;
